import { Box, Stack, Typography } from "@mui/material";
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AddEditTask, ObjectType } from "../../components";
import { BoardLayout } from "../../components/boardLayout";
import { IBoard, IItem } from "../../components/boardLayout/types";
import { useToggleView } from "../../hooks";
import { TagInfo } from "../../interfaces";
import { useAppDispatch } from "../../redux";
import { Scope, useLazyGetTagGroupsQuery, useLazyGetTagsBasedOnParameterQuery, useLazyGetTasksQuery, useUpdateTaskMutation } from "../../redux/services";
import { openDialog } from "../../redux/slices/dialog";
import { AppRoutes } from "../../router/routes";
import { DEFAULT_BOARD_VIEW_PAGINATION } from "../../utils";
import { getObjectType } from "../listview/addEditListView";
import { Dashboard } from "./dashboard";

export const TaskManager: React.FC<{ object_type: ObjectType, sequence_id: string, scope: Scope }> = ({ object_type, sequence_id, scope }) => {

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { id = "" } = useParams();
    const location = useLocation();

    const [getAllTasks] = useLazyGetTasksQuery({ refetchOnFocus: true });

    const [getTagGroups, tagGroupResponse] = useLazyGetTagGroupsQuery();
    const [getTags, tagsResponse] = useLazyGetTagsBasedOnParameterQuery();
    const [updateTask] = useUpdateTaskMutation();

    const [search, setSearch] = React.useState("");
    const { isBoardView, selectedBoard } = useToggleView("taskManager");

    const isLoading = tagGroupResponse.isFetching || tagGroupResponse.isLoading;

    const newTask = () => {
        dispatch(openDialog({
            title: "Create Task",
            hideNegativeBtn: true,
            hidePositiveBtn: true,
            body: <AddEditTask
                parentObjectId={id}
                parentObjectType={getObjectType(location) as string}
                isEdit={false}
                refetch={refetchTagGroups}
            />
        }));
    }

    const boardTypes = (tagsResponse?.data?.tags ?? []).map((t: any) => ({
        ...t, id: t._id, title: t.tag_name, description: t.tag_desc
    }));

    const refetchTagGroups = () => {
        getTagGroups({ ...DEFAULT_BOARD_VIEW_PAGINATION, scope });
    };

    React.useEffect(() => {
        if (isBoardView) {
            refetchTagGroups();
        }
        // eslint-disable-next-line
    }, [isBoardView]);

    React.useEffect(() => {
        if (selectedBoard?.canonical_name && tagGroupResponse.isSuccess) {
            getTags(selectedBoard?.canonical_name);
        }
        // eslint-disable-next-line
    }, [selectedBoard?._id, isLoading]);

    return <Box sx={{ height: "100%" }}>
        {isBoardView
            ?
            <BoardLayout
                showLayoutTitle={false}
                layoutTitleDetails={{
                    showTitle: false,
                    title: "Tasks",
                    breadcrumbs: [],
                    currentPath: "Task Manager"
                }}
                boardViewDetails={{
                    isLoading,
                    entityType: "taskManager",
                    boards: boardTypes,
                    stack: true,
                    onClick: (board: IBoard, item: IItem) => {
                        navigate(AppRoutes.viewTaskWithId(object_type, id, item?._id), { state: { sequence_id } })
                    },
                    refetchBoards: refetchTagGroups,
                    getTitle(d: IItem) {
                        return (
                            <Stack direction="row" spacing={1} sx={{
                                alignItems: "center"
                            }}>
                                <Typography 
                                    id="view-task"
                                    variant="caption"
                                    color="textSecondary">
                                    {d.title}
                                </Typography>
                            </Stack>
                        );
                    },
                    searchDetails: {
                        search,
                        onSearch: setSearch,
                        searchPlaceholder: "Search for Tasks"
                    },
                    tagFilterOption: { scope: scope },
                    creatable: true,
                    createBtnName: "Add Task",
                    onCreateBtn: () => newTask(),
                    fetchBoardItems: async (board: IBoard, page: number, rowsPerPage: number, search: string, filters) => {
                        const { data: boardData = { data: [], totalCount: 0 }, ...rest } = await getAllTasks({
                            search: search.trim() ? search : "",
                            page_no: page, page_size: rowsPerPage,
                            tag_id: [board.id, ...filters?.tags?.map((t) => t.value) ?? []].join(','),
                            parent_object_id: id, parent_object_type: object_type
                        });
                        return { ...boardData, ...rest };
                    },
                    onUpdateItemBoard: async (result, item: any) => {
                        const dropTag = boardTypes.find((b: IBoard) => b?.id === result?.destination?.droppableId);
                        if (dropTag) {
                            const updatedItem = {
                                name: item?.name,
                                parent: { object_id: id, object_type },
                                tags_info: [...(item.tags_info ?? []).filter((t: TagInfo) => t.tag_group_canonical_name !== (boardTypes?.[0] as any)?.tag_group_canonical_name), dropTag].map((t) => ({ tag_group_id: t.tag_group_id, tag_id: t.tag_id ?? t._id }))
                            }
                            return await updateTask({ id: item?._id, update_version: false, payload: updatedItem as any });
                        }
                        return new Promise((resolve, reject) => {
                            reject({ error: "Unable to move the Task" });
                        })
                    },
                }}
            />
            :
            <Dashboard object_type={object_type} sequence_id={sequence_id} />
        }
    </Box>
}
